import { API_URLS } from "@/api/urls";
import axiosInstance from "@/inteceptors";
import { withApiErrorHandling } from "@/utils/apiServiceHelpers";

export const loginUser = async (
  userData: IPropsLoginForm
): Promise<IResponse> => {
  return withApiErrorHandling(
    () => axiosInstance.post<IResponse>(API_URLS.AUTH.USER_LOGIN, userData),
    {
      context: "login",
      showNotification: false, // Login component handles notifications
    }
  );
};

export const logoutUser = async (
  data: IPropsLogout
): Promise<ISuccessResponse> => {
  return withApiErrorHandling(
    () => axiosInstance.post<ISuccessResponse>(API_URLS.AUTH.USER_LOGOUT, data),
    {
      context: "general",
      showNotification: false, // Usually handled by component
    }
  );
};

/**
 * Build an absolute, tenant-scoped API URL for the (unauthenticated) invite
 * accept flow. The shared admin app talks to a single API host, but invites can
 * belong to any tenant, so we prefix the request path with `/t/<slug>/` — the
 * public tenant-resolution mechanism the backend middleware understands. An
 * absolute URL makes axios ignore the request interceptor's baseURL override,
 * so this works whether or not central auth is enabled. Returns null when no
 * tenant slug is provided (falls back to the default-DB relative call).
 */
const tenantInviteUrl = (
  tenantSlug: string | undefined,
  relativePath: string
): string | null => {
  if (!tenantSlug) return null;
  const base = (import.meta.env.VITE_APP_BASE_URL as string) || "";
  // Strip a trailing "/api" (or "/api/") to get the host origin, then re-add it
  // after the /t/<slug> prefix: https://api.host/api/  ->  https://api.host/t/<slug>/api/
  const origin = base.replace(/\/api\/?$/, "");
  return `${origin}/t/${tenantSlug}/api${relativePath}`;
};

export const validateInvite = async (
  inviteToken: string,
  tenantSlug?: string
): Promise<ValidateInviteResponse> => {
  const relative = `${API_URLS.AUTH.VALIDATE_INVITE}${inviteToken}/validate/`;
  const url = tenantInviteUrl(tenantSlug, relative) ?? relative;
  const response = await axiosInstance.get<ValidateInviteResponse>(url);
  return response.data;
};

export const acceptInvite = async (
  data: AcceptInvitePayload
): Promise<InviteResponse> => {
  const { token, tenant, ...payload } = data;
  const relative = `${API_URLS.AUTH.ACCEPT_INVITE}${token}/`;
  const url = tenantInviteUrl(tenant, relative) ?? relative;
  const response = await axiosInstance.patch<InviteResponse>(url, {
    status: "accepted",
    ...payload,
  });
  return response.data;
};

export const resetPassword = async (data: {
  password: string;
  confirm_password: string;
  token: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
}): Promise<ISuccessResponse> => {
  const response = await axiosInstance.post<ISuccessResponse>(
    API_URLS.AUTH.RESET_PASSWORD,
    data
  );
  return response.data;
};

export const googleLogin = async (token: string) => {
  const response = await axiosInstance.post<{
    access: string;
    refresh: string;
    user: UserProfile;
  }>(API_URLS.AUTH.GOOGLE_LOGIN, {
    access_token: token,
  });
  return response.data;
};

export const linkedinLogin = async (code: string) => {
  const response = await axiosInstance.post<{
    access: string;
    refresh: string;
    user: UserProfile;
  }>(API_URLS.AUTH.LINKEDIN_LOGIN, {
    code: code,
  });
  return response.data;
};

export const forgetPassword = async (email: string) => {
  const response = await axiosInstance.post<ISuccessResponse>(
    API_URLS.AUTH.FORGET_PASSWORD,
    { email: email }
  );
  return response.data;
};
